/**
 * What a customer's answer is, and when it may be given.
 *
 * Pure. This is the module that decides whether a link is still good, whether
 * an answer is complete, and what gets written down — none of which should
 * depend on how the page looks or on being signed in. The customer is NOT a
 * user of the app: they hold a link, and the link is the whole of their
 * identity.
 *
 * The rule the rest of the product rests on: **an approval is recorded against
 * a version and a document hash.** If the estimate moved after the link was
 * sent, the link is stale and must refuse rather than quietly approve a total
 * the customer never saw.
 */

export type Choice = 'approved' | 'partly' | 'declined';

/** Why a link cannot be answered right now. `ready` is the only usable state. */
export type LinkState =
  | 'ready'
  | 'already_answered'
  | 'superseded'
  | 'expired'
  | 'revoked';

export interface LinkInput {
  /** Version and hash the link was issued against. */
  sentFor: { version: number; hash: string };
  /** Version and hash the estimate carries NOW. */
  current: { version: number; hash: string };
  /** Set once the customer has answered. */
  answeredAt: string | null;
  /** The shop can withdraw a link — a wrong figure, a cancelled job. */
  revoked: boolean;
  expiresAt: string | null;
  now: number;
}

export function linkState(input: LinkInput): LinkState {
  // Order matters. Revocation is the shop's deliberate act and outranks
  // everything; an answer already given outranks expiry, so a customer who
  // replied in time still sees their own answer a month later.
  if (input.revoked) return 'revoked';
  if (input.answeredAt) return 'already_answered';
  if (input.sentFor.version !== input.current.version
      || input.sentFor.hash !== input.current.hash) return 'superseded';
  if (input.expiresAt !== null) {
    const t = Date.parse(input.expiresAt);
    if (Number.isFinite(t) && t <= input.now) return 'expired';
  }
  return 'ready';
}

export interface Response {
  choice: Choice;
  /** What to leave out. Required for a partial approval, optional otherwise. */
  note: string;
}

export type ResponseProblem = 'note_required' | 'note_too_long';

export const NOTE_LIMIT = 2000;

/**
 * A partial approval without a note is not an answer.
 *
 * "Some of it" tells the shop nothing they can act on, and the estimator ends
 * up telephoning to ask — which is the call this whole flow exists to avoid.
 * Approved and declined need no note; the choice already says everything.
 */
export function validateResponse(r: Response): ResponseProblem | null {
  if (r.note.length > NOTE_LIMIT) return 'note_too_long';
  if (r.choice === 'partly' && r.note.trim() === '') return 'note_required';
  return null;
}

export interface RecordedApproval {
  choice: Choice;
  note: string;
  /** The exact document answered. Not a reference to "the estimate". */
  version: number;
  hash: string;
  at: string;
  /** What the shop may do next, decided here rather than in a screen. */
  next: 'invoice' | 'revise' | 'stop';
}

export function recordResponse(
  r: Response,
  document: { version: number; hash: string },
  at: string,
): RecordedApproval {
  return {
    choice: r.choice,
    // Trimmed, but never rewritten. It is the customer's own words and may be
    // read back to them.
    note: r.note.trim(),
    version: document.version,
    hash: document.hash,
    at,
    next: r.choice === 'approved' ? 'invoice'
      : r.choice === 'partly' ? 'revise'
      : 'stop',
  };
}

/** What the customer is told, in the language of the document. */
export function stateMessage(state: LinkState): { title: string; body: string } {
  switch (state) {
    case 'already_answered':
      return {
        title: 'You have already answered this',
        body: 'Your answer is shown below. To change it, contact the shop — an answer '
          + 'already given is part of the record.',
      };
    case 'superseded':
      return {
        title: 'This estimate has been updated',
        body: 'The shop revised it after sending this link, so the figures here are no '
          + 'longer the ones being proposed. A new link is on its way — nothing you do '
          + 'here would apply to the current version.',
      };
    case 'expired':
      return {
        title: 'This link has expired',
        body: 'Ask the shop to send it again. Nothing has been lost and no decision has '
          + 'been recorded.',
      };
    case 'revoked':
      return {
        title: 'This estimate has been withdrawn',
        body: 'The shop has taken it back. Contact them if you were expecting to approve '
          + 'work.',
      };
    case 'ready':
      return { title: '', body: '' };
  }
}
